import { useMutation, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";

import {
  TeamMemberTaskCategory,
  TeamMemberTaskPriority,
  TeamMemberTaskStatus,
  type CreateTeamMemberTaskDto,
} from "@fitness-app/data-models/entities/TeamMemberTask";
import { type Json } from "@fitness-app/supabase";
import { getErrorMessage } from "@fitness-app/utils";

import { useUserRole } from "~/hooks/trainer/useUserRole";
import { analyticsService, supabase } from "~/store/initializeStore";
import { TEAM_MEMBER_TASKS_QUERY_KEY } from "./useTeamMemberTasks";
import { TEAM_MEMBER_TASKS_COUNT_QUERY_KEY } from "./useTeamMemberTasksCount";
import { TEAM_MEMBER_TASKS_DUE_TODAY_PLANNED_COUNT_QUERY_KEY } from "./useTeamMemberTasksDueTodayOrPlannedCount";

export const useCreateTeamMemberTask = () => {
  const queryClient = useQueryClient();
  const { userId } = useUserRole();

  return useMutation({
    mutationFn: async (data: CreateTeamMemberTaskDto) => {
      // Prepare date fields - store TaskDateTime objects as JSONB
      const startDate = data.startDate ? (data.startDate as unknown as Json) : null;
      const endDate = data.endDate ? (data.endDate as unknown as Json) : null;
      const dueDate = data.dueDate ? (data.dueDate as unknown as Json) : null;

      const insertData = {
        title: data.title,
        description: data.description || null,
        priority: data.priority || TeamMemberTaskPriority.Medium,
        category: data.category || TeamMemberTaskCategory.Other,
        status: TeamMemberTaskStatus.Pending,
        startDate,
        endDate,
        dueDate,
        traineeId: data.traineeId || null,
        assignedTeamMemberId: data.assignedTeamMemberId ?? null,
        createdBy: userId,
        reminders: data.reminders ? (data.reminders as unknown as Json) : null,
        recurrence: data.recurrence ? (data.recurrence as unknown as Json) : null,
        metadata: data.metadata ? (data.metadata as unknown as Json) : null,
        tags: data.tags || [],
        createdAt: new Date().toISOString(),
      };

      const { data: created } = await supabase
        .from("team_member_task")
        .insert(insertData)
        .select()
        .single()
        .throwOnError();

      return created;
    },
    onSuccess: (_, variables) => {
      void message.success("Zadanie zostało utworzone");
      void queryClient.invalidateQueries({ queryKey: [TEAM_MEMBER_TASKS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [TEAM_MEMBER_TASKS_COUNT_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [TEAM_MEMBER_TASKS_DUE_TODAY_PLANNED_COUNT_QUERY_KEY] });
      void analyticsService.track("team_member_task_created", {
        category: variables.category,
        priority: variables.priority,
        hasTrainee: Boolean(variables.traineeId),
        hasDueDate: Boolean(variables.dueDate?.date),
        hasReminders: Boolean(variables.reminders?.length),
      });
    },
    onError: (error) => {
      void message.error(`Nie udało się utworzyć zadania: ${getErrorMessage(error)}`);
    },
  });
};
